/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { renderToStaticMarkup } from '@web-stories-wp/react';
import { dataPixels, PAGE_HEIGHT } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { TextOutputWithUnits } from '../elements/text/output';
import {
  calcFontMetrics,
  calcVerticalTrimFontMetrics,
  hasTextBorder,
} from '../elements/text/utils';
import {
  getBoundedTypographyScale,
  scaleInlineFontSizes,
} from '../elements/text/utils/mixedTypography';

const MEASURER_STYLES = {
  boxSizing: 'border-box',
  visibility: 'hidden',
  position: 'fixed',
  contain: 'layout paint',
  top: '-9999px',
  left: '-9999px',
  zIndex: -1,
  overflowWrap: 'break-word',
  // Rich text formatting can leave line breaks as text nodes inside spans.
  // Match the canvas text rendering so height recalculation keeps blank lines.
  whiteSpace: 'pre-wrap',
  ...(false && {
    // For debugging purposes - this will show the output render on screen
    background: 'red',
    visibility: 'visible',
    top: '99px',
    left: '99px',
    zIndex: 10000,
  }),
};

const MEASURER_PROPS = {
  dataToStyleX: (x) => `${x}px`,
  dataToStyleY: (y) => `${y}px`,
};

const MEASURER_NODE = '__WEB_STORIES_MEASURER__';
const LAST_ELEMENT = '__WEB_STORIES_LASTEL__';

export function calculateTextHeight(
  element,
  width,
  { dataToEditorX = (value) => value, dataToEditorY = (value) => value } = {}
) {
  const measurer = getOrCreateMeasurer(element);
  setStyles(measurer, { width: `${width}px`, height: null });

  if (
    element.verticalTrim === 'baseline' ||
    hasTextBorder(element.textBorder)
  ) {
    const fontMetrics = calcVerticalTrimFontMetrics({
      element: { ...element, width },
      dataToEditorX,
      dataToEditorY,
    });

    return element.verticalTrim === 'baseline'
      ? fontMetrics.height
      : fontMetrics.heightWithoutVerticalTrim;
  }

  const { standardTrimHeight } = calcFontMetrics({
    element: { ...element, width },
    dataToEditorX,
    dataToEditorY,
    calculateStandardTrimHeight: true,
  });

  return standardTrimHeight ?? measurer.parentNode.offsetHeight;
}

export function calculateFitTextFontSize({
  element,
  width,
  height,
  dataToEditorX,
  dataToEditorY,
}) {
  let measurer = getOrCreateMeasurer(element);
  setStyles(measurer, { width: `${width}px`, height: null, fontSize: null });

  // Binomial search for the best font size.
  let minFontSize = 1;
  let maxFontSize = element.fontSize
    ? element.fontSize *
      getBoundedTypographyScale(element, PAGE_HEIGHT / element.fontSize)
    : PAGE_HEIGHT;
  let marginOffsetTop;
  while (maxFontSize - minFontSize > 1) {
    const mid = dataPixels((minFontSize + maxFontSize) / 2);
    const requestedScale = element.fontSize ? mid / element.fontSize : 1;
    const scale = getBoundedTypographyScale(element, requestedScale);
    const candidateFontSize = element.fontSize ? element.fontSize * scale : mid;
    const candidateElement = {
      ...element,
      width,
      fontSize: candidateFontSize,
      content: scaleInlineFontSizes(element.content, scale),
    };
    const {
      marginOffsetTop: newMarginOffsetTop,
      marginOffsetBottom,
      height: verticalTrimHeight,
      standardTrimHeight,
    } = calcFontMetrics({
      element: { ...candidateElement, width },
      dataToEditorX,
      dataToEditorY,
      calculateStandardTrimHeight: true,
    });
    marginOffsetTop = newMarginOffsetTop;
    measurer = getOrCreateMeasurer(candidateElement);
    setStyles(measurer, {
      width: `${width}px`,
      fontSize: `${candidateFontSize}px`,
      margin: `${-marginOffsetTop}px 0`,
    });
    const currentHeight = measurer.offsetHeight;

    const newHeight = verticalTrimHeight
      ? verticalTrimHeight
      : standardTrimHeight ??
        currentHeight - marginOffsetTop - marginOffsetBottom;
    if (newHeight > height) {
      maxFontSize = mid;
    } else {
      minFontSize = mid;
    }
  }
  // This is for keeping the debug window in sync
  const finalScale = getBoundedTypographyScale(
    element,
    element.fontSize ? minFontSize / element.fontSize : 1
  );
  const finalFontSize = element.fontSize
    ? element.fontSize * finalScale
    : minFontSize;
  measurer = getOrCreateMeasurer({
    ...element,
    width,
    fontSize: finalFontSize,
    content: scaleInlineFontSizes(element.content, finalScale),
  });
  setStyles(measurer, {
    width: `${width}px`,
    fontSize: `${finalFontSize}px`,
    margin: `${-marginOffsetTop}px 0`,
  });
  return { fontSize: finalFontSize, marginOffsetTop };
}

function getOrCreateMeasurer(element) {
  let measurerNode = document.body[MEASURER_NODE];
  if (!measurerNode) {
    measurerNode = document.createElement('div');
    measurerNode.id = '__web-stories-text-measurer';
    measurerNode.className = 'web-stories-content';
    setStyles(measurerNode, MEASURER_STYLES);
    document.body.appendChild(measurerNode);
    document.body[MEASURER_NODE] = measurerNode;
  }
  // Very unfortunately `ReactDOM.render()` is not synchoronous. Thus, we
  // have to use `renderToStaticMarkup()` markup instead and do manual
  // diffing.
  if (changed(measurerNode, element)) {
    measurerNode.innerHTML = renderToStaticMarkup(
      <TextOutputWithUnits element={element} {...MEASURER_PROPS} />,
      measurerNode
    );
  }
  return measurerNode.firstElementChild;
}

function setStyles(node, styles) {
  for (const k in styles) {
    if (Object.prototype.hasOwnProperty.call(styles, k)) {
      const v = styles[k];
      if (v === null) {
        node.style[k] = '';
      } else {
        node.style[k] = v;
      }
    }
  }
}

function changed(node, element) {
  const lastElement = node[LAST_ELEMENT];
  node[LAST_ELEMENT] = element;
  if (!node.firstElementChild || !lastElement) {
    return true;
  }
  return lastElement !== element;
}
